



import React, { useCallback } from "react";
import CancelButton from "../../../Components/CommonComponent/Button/CancelButton";
import SaveButton from "../../../Components/CommonComponent/Button/SaveButton";
import { useTranslation } from "react-i18next";
import googleTransliterate from "../../../utils/googleTransliterate";
import { FaEdit } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { FaChevronDown } from "react-icons/fa";
import AllInButton from "../../../Components/CommonComponent/Button/Button";

const Dropdown = React.memo(function Dropdown({
  value,
  options,
  placeholder,
  onSelect,
  hasError,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [usingKeyboard, setUsingKeyboard] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const listRef = React.useRef(null);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setHighlightedIndex(-1);
        setUsingKeyboard(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (opt) => {
      onSelect(opt);
      setOpen(false);
      setHighlightedIndex(-1);
      setUsingKeyboard(false);
    },
    [onSelect]
  );

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      setUsingKeyboard(true);
      setHighlightedIndex(0);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setUsingKeyboard(true);
      setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setUsingKeyboard(true);
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        handleSelect(options[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlightedIndex(-1);
      setUsingKeyboard(false);
    }
  };

  React.useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const item = listRef.current.children[highlightedIndex];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  return (
    <div className="relative w-full mb-3" ref={dropdownRef}>
      <div
        tabIndex={0}
        onClick={() => { setOpen((prev) => !prev); setUsingKeyboard(false); }}
        onKeyDown={handleKeyDown}
        className={`border ${hasError ? "border-danger" : "border-gray-300"}
          rounded p-1 px-2 cursor-pointer bg-white focus:outline-none
          flex justify-between items-center`}
      >
        <span className="truncate">
          {value?.name || placeholder}
          {language !== "en" && value?.nameInHindi && ` (${value.nameInHindi})`}
        </span>
        <FaChevronDown className={`ml-2 text-sm transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <ul ref={listRef} className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded shadow max-h-48 overflow-y-auto p-0">
          {options.map((opt, i) => (
            <li
              key={i}
              onClick={() => handleSelect(opt)}
              onMouseEnter={() => { if (!usingKeyboard) setHighlightedIndex(i); }}
              className={`px-2 py-1 cursor-pointer ${highlightedIndex === i
                  ? "bg-theme-headerBg text-theme-color"
                  : !usingKeyboard
                    ? "hover:bg-theme-headerBg hover:text-theme-color"
                    : ""
                }`}
            >
              {opt.name} {language !== "en" && opt.nameInHindi && `(${opt.nameInHindi})`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default function CreateGroup({
  groupName,
  setGroupName,
  groupNameInHindi,
  setGroupNameInHindi,
  groupValidation,
  groupType,
  setGroupType,
  groupTypeValidation,
  setGroupTypeValidation,
  types,
  handleSave,
  handleCancel,
  setGroupValidation,
  selectedLedger,
  addMoreButton,
  loading,
  setLoading,
  editMode,
  groupBatch,
  setGroupBatch,
}) {
  const { t } = useTranslation();
  const [showField, setShowField] = React.useState(false);
  const [hindiSuggestion, setHindiSuggestion] = React.useState("");
  const [editingBatchIndex, setEditingBatchIndex] = React.useState(-1);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  // ─── Transliteration ──────────────────────────────────────────────────────
  const handleGroupNameChange = async (e) => {
    const value = e.target.value;
    setGroupName(value);
    setHindiSuggestion(value);

    try {
      if (language !== "en") {
        const result = await googleTransliterate(value, language);

        if (result?.suggestions) {
          setSuggestions(result.suggestions.slice(0, 4)); // only 4
          setGroupNameInHindi(result.suggestions[0] || "");
        } else {
          setSuggestions([]);
          setGroupNameInHindi("");
        }

        setShowSuggestions(true);
      }
    } catch (err) {
      console.error("Transliteration error:", err);
    }
  };

  const handleConvertNameInHindi = async (e) => {
    const value = e.target.value;
    setHindiSuggestion(value);

    try {
      const result = await googleTransliterate(value, language);

      if (result?.suggestions) {
        setSuggestions(result.suggestions.slice(0, 3));
        setGroupNameInHindi(result.suggestions[0] || "");
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error("Transliteration error:", err);
    }
  };

  const handleSelectType = useCallback(
    (type) => {
      setGroupType(type);
      setGroupTypeValidation(false);
    },
    [setGroupType, setGroupTypeValidation]
  );

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    if (!groupName.trim()) { setGroupValidation(true); return false; }
    if (!groupType) { setGroupTypeValidation(true); setGroupValidation(false); return false; }
    return true;
  };

  // ─── Reset current form fields only ──────────────────────────────────────
  const resetFields = () => {
    setGroupName("");
    setGroupNameInHindi("");
    setHindiSuggestion("");
    setGroupType(types[0]);
    setShowField(false);
    setGroupValidation(false);
    setGroupTypeValidation(false);
    setEditingBatchIndex(-1);
  };

  // ─── Add More (batch) ─────────────────────────────────────────────────────
  const handleAddMore = () => {
    if (!validate()) return;

    const newGroup = {
      groupName,
      groupNameInHindi,
      type: groupType.name,
      typeInHindi: groupType.nameInHindi,
      _groupType: groupType, // for restoring dropdown
    };

    if (editingBatchIndex >= 0) {
      // update existing batch item
      setGroupBatch((prev) => {
        const updated = [...prev];
        updated[editingBatchIndex] = newGroup;
        return updated;
      });
    } else {
      setGroupBatch((prev) => [...prev, newGroup]);
    }

    resetFields();
  };

  // ─── Edit a batch item ────────────────────────────────────────────────────
  const handleEditBatchItem = (index) => {
    const item = groupBatch[index];
    setEditingBatchIndex(index);
    setGroupName(item.groupName);
    setGroupNameInHindi(item.groupNameInHindi || "");
    setHindiSuggestion(item.groupName);
    setGroupType(item._groupType || types.find((t) => t.name === item.type) || types[0]);
    setGroupValidation(false);
    setGroupTypeValidation(false);
  };

  // ─── Remove a batch item ──────────────────────────────────────────────────
  const handleRemoveBatchItem = (index) => {
    setGroupBatch((prev) => prev.filter((_, i) => i !== index));
    if (editingBatchIndex === index) {
      resetFields();
    } else if (editingBatchIndex > index) {
      setEditingBatchIndex((prev) => prev - 1);
    }
  };

  // ─── Save All ─────────────────────────────────────────────────────────────
  // Parent's handleSave already handles both single + batch via groupBatch state
  const handleSaveAll = () => {
    if (editMode) {
      // Edit mode: just validate current form and call parent save
      if (!validate()) return;
      handleSave();
      return;
    }

    // If batch has items and current form is also filled → add current to batch first
    if (groupBatch && groupBatch.length > 0) {
      // If current form has data, merge it
      if (groupName.trim()) {
        if (!validate()) return;
        const currentGroup = {
          groupName,
          groupNameInHindi,
          type: groupType.name,
          typeInHindi: groupType.nameInHindi,
          _groupType: groupType,
        };
        if (editingBatchIndex >= 0) {
          const updated = [...groupBatch];
          updated[editingBatchIndex] = currentGroup;
          setGroupBatch(updated);
        } else {
          setGroupBatch((prev) => [...prev, currentGroup]);
        }
      }
      // Let parent save all batch
      handleSave();
    } else {
      // No batch — save single current form
      if (!validate()) return;
      handleSave();
    }
  };

  const batchLength = groupBatch?.length ?? 0;

  return (
    <div className="bg-theme-tableBg p-2 rounded shadow-lg w-96 z-[100]">
      {/* Header */}
      <div className="bg-theme-headerBg text-theme-color rounded-1 px-2 py-1 mb-2 flex justify-between items-center">
        <div className="font-medium py-[2px]">
          {editMode ? t("editGroup") : t("addNewGroup")}
        </div>
        {/* {batchLength > 0 && (
          <span className="text-xs bg-white text-theme-headerBg px-2 py-[2px] rounded font-medium">
            {batchLength} added
          </span>
        )} */}
      </div>

      {/*  Batch List */}
      {batchLength > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-theme-headerBg mb-1">
            {t("listOfAddedGroups") || "Added Groups"} ({batchLength})
          </div>
          <div className="flex flex-col gap-1 max-h-32 overflow-y-auto pr-1">
            {groupBatch.map((item, index) => (
              <div
                key={index}
                className={`flex items-center justify-between rounded border px-2 py-[4px] text-xs transition-all
                  ${editingBatchIndex === index
                    ? "border-theme-headerBg bg-theme-headerBg/10 font-medium"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                  }`}
              >
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <span className="text-gray-400 w-4 shrink-0">{index + 1}.</span>
                  <span className="truncate font-medium text-gray-800 capitalize">
                    {item.groupName}
                    {item.groupNameInHindi && (
                      <span className="text-gray-400 font-normal ml-1">({item.groupNameInHindi})</span>
                    )}
                  </span>
                  <span className="text-gray-400 shrink-0">• {item.type}</span>
                </div>
                <div className="flex items-center gap-1 ml-1 shrink-0">
                  {editingBatchIndex === index ? (
                    <span className="text-theme-headerBg italic">{t("editing") || "editing..."}</span>
                  ) : (
                    <button type="button" onClick={() => handleEditBatchItem(index)}
                      className="text-gray-500 hover:text-theme-headerBg p-1 rounded transition-colors">
                      <FaEdit className="text-xs" />
                    </button>
                  )}
                  <button type="button" onClick={() => handleRemoveBatchItem(index)}
                    className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors">
                    <ImCross className="text-[10px]" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editing banner */}
      {/* {editingBatchIndex >= 0 && (
        <div className="mb-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex justify-between items-center">
          <span>✏️ Editing group #{editingBatchIndex + 1}</span>
          <button type="button" onClick={resetFields} className="underline hover:text-blue-900">
            Cancel Edit
          </button>
        </div>
      )} */}

      {/* Group Name Input */}
      <label className="font-medium text-sm">{t("groupName")}</label>
      <input
        type="text"
        value={groupName}
        onChange={handleGroupNameChange}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder="Enter group name"
        className={`border ${groupValidation ? "border-danger" : ""} outline-none capitalize rounded w-full p-1 px-2 mb-1`}
      />
      {groupValidation && (
        <div className="text-theme-createLedgerColor text-sm px-2 capitalize mb-1">
          {t("groupNameIsRequired")}
        </div>
      )}

      {/* Hindi edit field */}
      {showField && (
        <div>
          <label className="font-medium text-sm"> {t('enterTranslateValue')}</label>
          <div className="flex mb-2">
            <input type="text" value={hindiSuggestion} onChange={handleConvertNameInHindi} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Enter name"
              className="border outline-none rounded-s w-full p-1 px-2 capitalize" />
            <div className="border px-2 ms-auto w-8 cursor-pointer rounded-e bg-white">
              <ImCross className="mt-2 text-theme-createLedgerColor" onClick={() => setShowField(false)} />
            </div>
          </div>
        </div>
      )}
      {language !== "en" && (
        <div className="relative">
          <label className="block font-medium text-sm pb-1">
            {language === "hi" ? t("translateinHindi") : t("translateinPunjabi")}
          </label>
          <div className="flex mb-2">
            <input type="text" value={groupNameInHindi} disabled placeholder="Translate Value"
              className="border px-2 py-1 rounded-s w-full cursor-not-allowed bg-gray-100 capitalize" />
            <div className="border px-2 ms-auto w-8 cursor-pointer rounded-e bg-white">
              <FaEdit className="mt-2" onClick={() => setShowField(true)} />
            </div>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <ul className="border rounded bg-white shadow absolute z-20 w-full text-sm p-0">
              {suggestions.slice(0, 4).map((s, i) => (
                <li
                  key={i}
                  className="px-2 py-1 cursor-pointer hover:bg-theme-headerBg hover:text-theme-color"
                  onClick={() => {
                    setGroupNameInHindi(s);
                    setShowSuggestions(false);
                  }}
                >
                  {s}
                </li>
              ))}
            </ul>
          )}

        </div>
      )}


      {/* Type Dropdown */}
      <label className="font-medium block text-sm">{t("selectType")}</label>
      <Dropdown
        value={groupType}
        placeholder="Select group type"
        hasError={groupTypeValidation}
        options={types}
        onSelect={handleSelectType}
      />
      {groupTypeValidation && (
        <div className="text-red-600 text-sm px-2 mb-1">{t("selectTheType")}</div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end items-center mt-3 pt-3">
        {/* <div>
          {batchLength > 0 && (
            <span className="text-xs text-gray-500">
              Total: {batchLength} group{batchLength > 1 ? "s" : ""}
            </span>
          )}
        </div> */}
        <div className="flex gap-2">
          <AllInButton onClick={handleCancel} text={t("cancel")} />
          {/* Add More: only in create mode, and only when addMoreButton=true */}
          {addMoreButton && !editMode && (
            <AllInButton
              onClick={handleAddMore}
              text={editingBatchIndex >= 0 ? (t("edit") || "Update") : (t("addMore") || "Add More")}
            />
          )}
          <AllInButton
            onClick={handleSaveAll}
            text={
              batchLength > 0
                ? t("saveAll")
                : loading
                  ? (t("processing") || "Processing...")
                  : t("save")
            }
          />
        </div>
      </div>
    </div>
  );
}